/**
 * FinOps API Service
 * 
 * Service for fetching purchase request data from the FinOps (Access Financials Operations) API.
 * This service routes requests through the backend (FE -> BE -> FinOps API) for security.
 * This service is used when a company has the FinOps integration configured.
 */

import type { FinOpsPurchaseRequestHeader, FinOpsPurchaseRequestDetail, FinOpsApiConfig, FinOpsTimesheetEntry, FinOpsApprovalHistoryEntry, FinOpsProjectDetails } from '@/types/finops.types';
import type { PurchaseRequest, PurchaseRequestStatus } from '@/types/purchase-request';
import { getIntegrationsFromStorage } from '@/hooks/use-product-integrations';
import { getApiBaseUrl, getDataMode } from './data-service';
import { apiFetch } from './api-error-handler';

const FINOPS_PRODUCT_CODE = 'access-financials-operations';

/**
 * Get the FinOps API configuration if the integration is set up for a specific company
 * Returns null if no FinOps integration is configured for the company
 * @param companyId - Optional company ID to filter integrations. If not provided, returns first connected integration.
 */
export function getFinOpsConfig(companyId?: number): FinOpsApiConfig | null {
  const integrations = getIntegrationsFromStorage();
  
  const finOpsIntegration = integrations.find(
    (integration) => 
      integration.productCode === FINOPS_PRODUCT_CODE && 
      integration.status === 'connected' &&
      (companyId ? integration.companyId === String(companyId) : true)
  );
  
  if (!finOpsIntegration) {
    return null;
  }
  
  return {
    baseUrl: getApiBaseUrl(),
    instanceId: finOpsIntegration.instanceId,
  };
}

/**
 * Check if FinOps integration is available for a specific company
 * @param companyId - Optional company ID to check. If not provided, checks for any connected integration.
 */
export function isFinOpsIntegrationAvailable(companyId?: number): boolean {
  return getFinOpsConfig(companyId) !== null;
}

/**
 * Map FinOps API status to internal PurchaseRequestStatus
 */
function mapFinOpsStatus(status: string): PurchaseRequestStatus {
  switch (status) {
    case 'Approved':
    case 'Ordered':
    case 'Received':
      return 'Approved';
    case 'Posted':
      return 'Posted';
    case 'Rejected':
      return 'Rejected';
    case 'Cancelled':
      return 'Cancelled';
    case 'AwaitingApproval':
    case 'Awaiting Approval':
      return 'Awaiting Approval';
    case 'Draft':
      return 'Draft';
    case 'Pending':
    case 'NotSet':
    case 'Submitted':
      return 'Pending';
    default:
      console.warn(`[mapFinOpsStatus] Unrecognised status: "${status}", defaulting to Pending`);
      return 'Pending';
  }
}

/**
 * PRSH_POSTING_TYPE value for a Financials Purchase Order Credit.
 */
const POSTING_TYPE_PO_CREDIT = 5;

/**
 * Determines whether a FinOps PR represents a credit (Purchase Ledger Credit
 * or Purchase Order Credit). Credits are stored as positive values in FinOps
 * but must be shown as negative in the Projects context (they reduce
 * committed/logged cost).
 */
function isCreditType(header: FinOpsPurchaseRequestHeader): boolean {
  if (header.postingType === POSTING_TYPE_PO_CREDIT) {
    return true;
  }
  const normalised = (header.type || '').toLowerCase();
  return (
    normalised.includes('pl credit') ||
    normalised.includes('purchase ledger credit') ||
    normalised.includes('po credit') ||
    normalised.includes('purchase order credit')
  );
}

/** PRSH_POSTING_TYPE values that should be hidden from the Purchases tab. */
const EXCLUDED_POSTING_TYPES = new Set([
  4,  // Project Forecast Cost Adjustment
  8,  // Create as Memo Only
]);

const EXCLUDED_POSTING_TYPE_LABELS = new Set([
  'project forecast cost adjustment',
  'memo only',
  'create as memo only',
]);

function isExcludedPostingType(header: FinOpsPurchaseRequestHeader): boolean {
  if (header.postingType != null && EXCLUDED_POSTING_TYPES.has(header.postingType)) {
    return true;
  }
  if (header.type) {
    const normalised = header.type.toLowerCase().trim();
    if (EXCLUDED_POSTING_TYPE_LABELS.has(normalised)) {
      return true;
    }
  }
  return false;
}

/**
 * Map FinOps API response to internal PurchaseRequest format
 */
function mapFinOpsToPurchaseRequest(
  header: FinOpsPurchaseRequestHeader,
  projectId: number
): PurchaseRequest {
  const rawAmount = header.total || 0;
  // Credits (PL Credit / PO Credit) come through as positive from FinOps but represent negative cost
  const sign = isCreditType(header) ? -1 : 1;
  const amount = sign * Math.abs(rawAmount);

  return {
    id: `finops-${header.primaryKey}`,
    requestId: header.reference || `PR-${header.primaryKey}`,
    description: header.description || '',
    status: mapFinOpsStatus(header.status),
    amount,
    net: header.net ? sign * Math.abs(header.net) : undefined,
    vat: header.vat ? sign * Math.abs(header.vat) : undefined,
    gross: header.gross ? sign * Math.abs(header.gross) : undefined,
    supplier: header.accountCode || header.accountName || '',
    supplierName: header.accountName || '',
    date: header.orderDate,
    projectId: String(projectId),
    requestedBy: header.initiator || 'Unknown',
    approvedBy: header.status === 'Approved' ? header.initiator : undefined,
    approvedDate: header.status === 'Approved' ? header.lastEdited : undefined,
    category: header.type || 'General',
    notes: header.orderNumber ? `Order Number: ${header.orderNumber}` : undefined,
    poNumber: header.orderNumber ? [header.orderNumber] : undefined,
    currency: 'GBP',
    documents: [],
    lines: undefined,
    costCentreCode: header.costCentre || undefined,
    finOpsPrimaryKey: header.primaryKey,
    splitLine: header.splitLine,
  };
}

/**
 * Fetch purchase requests from the FinOps API via the backend
 * Routes through: Frontend -> Backend -> FinOps API
 * 
 * @param projectCode - The project code to fetch purchase requests for
 * @param projectId - The internal project ID for mapping
 * @param companyId - Optional company ID to use for determining which FinOps instance to query (demo mode)
 * @param instanceId - Optional instance ID to use directly (live mode - from company.financialsInstanceId for purchases)
 * @returns Array of PurchaseRequest objects or null if integration is not available
 */
export async function fetchFinOpsPurchaseRequests(
  projectCode: string,
  projectId: number,
  companyId?: number,
  instanceId?: string
): Promise<PurchaseRequest[] | null> {
  const dataMode = getDataMode();
  
  // In live mode, use the provided instanceId directly
  // In demo mode, look up the config from localStorage
  let config: FinOpsApiConfig | null;
  if (dataMode === 'live' && instanceId) {
    config = {
      baseUrl: getApiBaseUrl(),
      instanceId: instanceId,
    };
  } else {
    config = getFinOpsConfig(companyId);
  }
  
  if (!config) {
    return null;
  }
  
  if (dataMode !== 'live') {
    return null;
  }
  
  // Validate that instanceId is present - required for FinOps API
  if (!config.instanceId) {
    throw new Error('FinOps integration requires an instanceId. Please configure the company with a valid FinOps instance.');
  }
  
  try {
    // Call the backend endpoint which proxies to FinOps API
    // Note: config.baseUrl already includes /api, so we don't add it again
    const url = `${config.baseUrl}/finops/purchase-requests?projectCode=${encodeURIComponent(projectCode)}`;
    
    // Use X-FinOps-Instance-Id header for the company's FinOps instance ID
    // This is separate from X-Instance-Id which is used for platform subscription lookup
    const data = await apiFetch<FinOpsPurchaseRequestHeader[] | { items?: FinOpsPurchaseRequestHeader[]; data?: FinOpsPurchaseRequestHeader[]; value?: FinOpsPurchaseRequestHeader[]; $values?: FinOpsPurchaseRequestHeader[] }>(url, {
      method: 'GET',
      headers: {
        'X-FinOps-Instance-Id': config.instanceId,
      },
    });
    
    let headers: FinOpsPurchaseRequestHeader[] = [];
    if (Array.isArray(data)) {
      headers = data;
    } else if (data && typeof data === 'object') {
      // Try various common response wrapper formats
      const dataObj = data as Record<string, unknown>;
      if (Array.isArray(dataObj.list)) {
        // FinOps API returns data in 'list' property
        headers = dataObj.list as FinOpsPurchaseRequestHeader[];
      } else if (Array.isArray(dataObj.items)) {
        headers = dataObj.items as FinOpsPurchaseRequestHeader[];
      } else if (Array.isArray(dataObj.data)) {
        headers = dataObj.data as FinOpsPurchaseRequestHeader[];
      } else if (Array.isArray(dataObj.value)) {
        headers = dataObj.value as FinOpsPurchaseRequestHeader[];
      } else if (Array.isArray(dataObj.$values)) {
        headers = dataObj.$values as FinOpsPurchaseRequestHeader[];
      } else if (Array.isArray(dataObj.results)) {
        headers = dataObj.results as FinOpsPurchaseRequestHeader[];
      } else if (Array.isArray(dataObj.records)) {
        headers = dataObj.records as FinOpsPurchaseRequestHeader[];
      }
    }
    
    return headers
      .filter((header) => !isExcludedPostingType(header))
      .map((header) => mapFinOpsToPurchaseRequest(header, projectId));
  } catch (error) {
    console.error('Error fetching FinOps purchase requests:', error);
    throw error;
  }
}

/**
 * Fetch purchase request approval history from the FinOps API via the backend
 * Routes through: Frontend -> Backend -> FinOps API
 * 
 * @param recordLink - The record link ID (header primaryKey for non-split-line, detail primaryKey for split-line)
 * @param instanceId - The FinOps instance ID (from company.financialsInstanceId)
 * @returns Array of FinOpsApprovalHistoryEntry objects or null if not available
 */
export async function fetchFinOpsPurchaseRequestApprovalHistory(
  recordLink: number,
  instanceId: string
): Promise<FinOpsApprovalHistoryEntry[] | null> {
  const dataMode = getDataMode();
  
  if (dataMode !== 'live') {
    return null;
  }
  
  if (!instanceId || recordLink <= 0) {
    return null;
  }
  
  try {
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/finops/purchase-request-approval-history?recordLink=${recordLink}`;
    
    const data = await apiFetch<FinOpsApprovalHistoryEntry[] | { list?: FinOpsApprovalHistoryEntry[]; items?: FinOpsApprovalHistoryEntry[]; data?: FinOpsApprovalHistoryEntry[] }>(url, {
      method: 'GET',
      headers: {
        'X-FinOps-Instance-Id': instanceId,
      },
    });
    
    let entries: FinOpsApprovalHistoryEntry[] = [];
    if (Array.isArray(data)) {
      entries = data;
    } else if (data && typeof data === 'object') {
      const dataObj = data as Record<string, unknown>;
      if (Array.isArray(dataObj.list)) {
        entries = dataObj.list as FinOpsApprovalHistoryEntry[];
      } else if (Array.isArray(dataObj.items)) {
        entries = dataObj.items as FinOpsApprovalHistoryEntry[];
      } else if (Array.isArray(dataObj.data)) {
        entries = dataObj.data as FinOpsApprovalHistoryEntry[];
      } else if (Array.isArray(dataObj.value)) {
        entries = dataObj.value as FinOpsApprovalHistoryEntry[];
      } else if (Array.isArray(dataObj.$values)) {
        entries = dataObj.$values as FinOpsApprovalHistoryEntry[];
      } else if (Array.isArray(dataObj.results)) {
        entries = dataObj.results as FinOpsApprovalHistoryEntry[];
      } else if (Array.isArray(dataObj.records)) {
        entries = dataObj.records as FinOpsApprovalHistoryEntry[];
      }
    }
    
    return entries;
  } catch (error) {
    console.error('Error fetching FinOps purchase request approval history:', error);
    throw error;
  }
}

/**
 * Fetch purchase request line details from the FinOps API via the backend
 * Routes through: Frontend -> Backend -> FinOps API
 * 
 * @param projectCode - The project code to fetch line details for
 * @param instanceId - The FinOps instance ID (from company.financialsInstanceId for purchases)
 * @returns Array of FinOpsPurchaseRequestDetail objects or null if not available
 */
export async function fetchFinOpsPurchaseRequestDetails(
  projectCode: string,
  instanceId: string
): Promise<FinOpsPurchaseRequestDetail[] | null> {
  const dataMode = getDataMode();
  
  if (dataMode !== 'live') {
    return null;
  }
  
  if (!instanceId) {
    return null;
  }
  
  try {
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/finops/purchase-request-details?projectCode=${encodeURIComponent(projectCode)}`;
    
    const data = await apiFetch<FinOpsPurchaseRequestDetail[] | { list?: FinOpsPurchaseRequestDetail[]; items?: FinOpsPurchaseRequestDetail[]; data?: FinOpsPurchaseRequestDetail[] }>(url, {
      method: 'GET',
      headers: {
        'X-FinOps-Instance-Id': instanceId,
      },
    });
    
    let details: FinOpsPurchaseRequestDetail[] = [];
    if (Array.isArray(data)) {
      details = data;
    } else if (data && typeof data === 'object') {
      const dataObj = data as Record<string, unknown>;
      if (Array.isArray(dataObj.list)) {
        details = dataObj.list as FinOpsPurchaseRequestDetail[];
      } else if (Array.isArray(dataObj.items)) {
        details = dataObj.items as FinOpsPurchaseRequestDetail[];
      } else if (Array.isArray(dataObj.data)) {
        details = dataObj.data as FinOpsPurchaseRequestDetail[];
      } else if (Array.isArray(dataObj.value)) {
        details = dataObj.value as FinOpsPurchaseRequestDetail[];
      } else if (Array.isArray(dataObj.$values)) {
        details = dataObj.$values as FinOpsPurchaseRequestDetail[];
      } else if (Array.isArray(dataObj.results)) {
        details = dataObj.results as FinOpsPurchaseRequestDetail[];
      } else if (Array.isArray(dataObj.records)) {
        details = dataObj.records as FinOpsPurchaseRequestDetail[];
      }
    }
    
    return details;
  } catch (error) {
    console.error('Error fetching FinOps purchase request details:', error);
    throw error;
  }
}

/**
 * Fetch timesheet entries from the FinOps API via the backend
 * Routes through: Frontend -> Backend -> FinOps API
 * 
 * @param projectCode - The project code to fetch timesheets for
 * @param instanceId - The FinOps instance ID (from company.timesheetInstanceId for timesheets)
 * @returns Array of FinOpsTimesheetEntry objects or null if not available
 */
export async function fetchFinOpsTimesheets(
  projectCode: string,
  instanceId: string
): Promise<FinOpsTimesheetEntry[] | null> {
  const dataMode = getDataMode();
  
  if (dataMode !== 'live') {
    return null;
  }
  
  if (!instanceId) {
    return null;
  }
  
  try {
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/finops/timesheets?projectCode=${encodeURIComponent(projectCode)}`;
    
    const data = await apiFetch<FinOpsTimesheetEntry[] | { list?: FinOpsTimesheetEntry[]; items?: FinOpsTimesheetEntry[]; data?: FinOpsTimesheetEntry[] }>(url, {
      method: 'GET',
      headers: {
        'X-FinOps-Instance-Id': instanceId,
      },
    });
    
    let entries: FinOpsTimesheetEntry[] = [];
    if (Array.isArray(data)) {
      entries = data;
    } else if (data && typeof data === 'object') {
      const dataObj = data as Record<string, unknown>;
      if (Array.isArray(dataObj.list)) {
        entries = dataObj.list as FinOpsTimesheetEntry[];
      } else if (Array.isArray(dataObj.items)) {
        entries = dataObj.items as FinOpsTimesheetEntry[];
      } else if (Array.isArray(dataObj.data)) {
        entries = dataObj.data as FinOpsTimesheetEntry[];
      } else if (Array.isArray(dataObj.value)) {
        entries = dataObj.value as FinOpsTimesheetEntry[];
      } else if (Array.isArray(dataObj.$values)) {
        entries = dataObj.$values as FinOpsTimesheetEntry[];
      } else if (Array.isArray(dataObj.results)) {
        entries = dataObj.results as FinOpsTimesheetEntry[];
      } else if (Array.isArray(dataObj.records)) {
        entries = dataObj.records as FinOpsTimesheetEntry[];
      }
    }
    
    return entries;
  } catch (error) {
    console.error('Error fetching FinOps timesheets:', error);
    throw error;
  }
}

/**
 * Fetch project-level financial details from the FinOps API via the backend.
 * Routes through: Frontend -> Backend -> FinOps API (GET /fins/projectdetails).
 *
 * Returns the project details object (actualSales, actualCost, etc.) or null
 * when unavailable (not live mode, no instanceId, or empty response).
 *
 * @param projectCode - The project code to fetch details for
 * @param instanceId - The FinOps instance ID (from company.financialsInstanceId)
 */
export async function fetchFinOpsProjectDetails(
  projectCode: string,
  instanceId: string
): Promise<FinOpsProjectDetails | null> {
  const dataMode = getDataMode();

  if (dataMode !== 'live') {
    return null;
  }

  if (!instanceId || !projectCode) {
    return null;
  }

  try {
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/finops/projectdetails?projectCode=${encodeURIComponent(projectCode)}`;

    const data = await apiFetch<FinOpsProjectDetails>(url, {
      method: 'GET',
      headers: {
        'X-FinOps-Instance-Id': instanceId,
      },
    });

    if (!data || typeof data.actualSales !== 'number' || typeof data.actualCost !== 'number') {
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error fetching FinOps project details:', error);
    throw error;
  }
}

/**
 * Fetch the configured FinOps web base URL from the backend.
 * Returns null when not configured.
 */
export async function fetchFinOpsWebUrl(): Promise<string | null> {
  try {
    const baseUrl = getApiBaseUrl();
    const data = await apiFetch<{ webUrl: string | null }>(`${baseUrl}/finops/web-url`, {
      method: 'GET',
    });
    return data?.webUrl ?? null;
  } catch {
    return null;
  }
}
